import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import {
  FILTER_OPERATORS,
  QUEUE_MODES,
  makeSelectionItem,
  buildQueryFragment,
  generateFilterId,
  matchFilter,
} from '@/utils/selection'
import { useDataSourcesStore } from '@product/stores/dataSources'

// useCellSelection
//
// Spreadsheet / Kibana-style cell selection for the results tables:
//   plain click     -> select one cell (sets the anchor)
//   ctrl/cmd click   -> toggle a cell in/out of the selection (moves the anchor)
//   shift click      -> rectangular area select from the anchor to the target
//
// The host owns row/column ordering (TanStack row model or a plain index grid),
// so the host computes the rectangle and hands the resolved cells to setRange().
// The composable stays table-agnostic: it just tracks which cells are selected
// and exposes their selection items (the shape shared with the apply logic and
// the context menu).
//
// A "cell" passed in is a resolved descriptor:
//   { rowId, colId, rowIndex, colIndex, field, rawValue, formattedValue, mode? }
export function useCellSelection() {
  const router = useRouter()
  const dataSourcesStore = useDataSourcesStore()

  // cellKey -> stored cell (item fields + rowId/colId/indices)
  const selection = ref(new Map())
  // { rowId, colId, rowIndex, colIndex } — origin for shift-range
  const anchor = ref(null)

  const cellKey = (rowId, colId) => `${rowId}::${colId}`

  function toStored(cell) {
    const item = makeSelectionItem(
      cell.field,
      cell.rawValue,
      cell.formattedValue,
      cell.mode || QUEUE_MODES.FIELD_VALUE,
      cell.empty || false,
    )
    return {
      ...item,
      rowId: cell.rowId,
      colId: cell.colId,
      rowIndex: cell.rowIndex,
      colIndex: cell.colIndex,
    }
  }

  function isSelected(rowId, colId) {
    return selection.value.has(cellKey(rowId, colId))
  }

  const hasSelection = computed(() => selection.value.size > 0)
  const count = computed(() => selection.value.size)

  // Distinct-by-valueKey items for apply / menu (identical field:value across
  // cells collapses to one filter). First occurrence wins.
  const selectedItems = computed(() => {
    const seen = new Set()
    const out = []
    for (const stored of selection.value.values()) {
      if (seen.has(stored.valueKey)) continue
      seen.add(stored.valueKey)
      out.push(stored)
    }
    return out
  })

  function setSingle(cell) {
    const stored = toStored(cell)
    selection.value = new Map([[cellKey(cell.rowId, cell.colId), stored]])
    anchor.value = { rowId: cell.rowId, colId: cell.colId, rowIndex: cell.rowIndex, colIndex: cell.colIndex }
  }

  // Plain click: collapse to a single selection of the clicked cell. Clicking
  // the cell that is *already the sole selection* clears it (toggle-off). A
  // plain click on one cell of a multi-selection replaces the whole selection
  // — the standard-table behavior, not "remove just this one".
  function setSingleOrDeselect(cell) {
    const key = cellKey(cell.rowId, cell.colId)
    if (selection.value.size === 1 && selection.value.has(key)) {
      clear()
      return
    }
    setSingle(cell)
  }

  function toggle(cell) {
    const key = cellKey(cell.rowId, cell.colId)
    const next = new Map(selection.value)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.set(key, toStored(cell))
    }
    selection.value = next
    anchor.value = { rowId: cell.rowId, colId: cell.colId, rowIndex: cell.rowIndex, colIndex: cell.colIndex }
  }

  // Replace the selection with a resolved rectangle (host-enumerated). The
  // anchor is left untouched so successive shift-clicks re-extend from it.
  function setRange(cells) {
    const next = new Map()
    for (const cell of cells) {
      if (!cell) continue
      next.set(cellKey(cell.rowId, cell.colId), toStored(cell))
    }
    selection.value = next
  }

  function clear() {
    selection.value = new Map()
    anchor.value = null
  }

  // --- applied filters -------------------------------------------------------
  //
  // The client-side-filter tables (IP bulk, generic selector) accumulate
  // removable AND/OR/NOT filter chips and match them against their already-
  // loaded rows. That state lives here so a parent that owns the composable can
  // read and manipulate it (tool rail, export scope, clear-all). Server-query
  // tables (event search, aggregations) ignore this and emit `apply-filter`
  // instead — their applied filters are the parent's Lucene query.

  // [{ id, items, operator }]
  const appliedFilters = ref([])

  function pushApplied(items, operator) {
    if (!items.length) return
    appliedFilters.value = [
      ...appliedFilters.value,
      { id: generateFilterId(), items: items.map(i => ({ ...i })), operator },
    ]
  }

  function removeApplied(id) {
    appliedFilters.value = appliedFilters.value.filter(f => f.id !== id)
  }

  // Remove one item (by valueKey) from every applied filter; drop emptied groups.
  // The invariant: a value lives in exactly one filter, so before adding it under
  // a new operator we clear any prior occurrence (include or exclude).
  function dropValueKey(vk) {
    appliedFilters.value = appliedFilters.value
      .map(f => ({ ...f, items: f.items.filter(i => i.valueKey !== vk) }))
      .filter(f => f.items.length > 0)
  }

  function clearApplied() {
    appliedFilters.value = []
  }

  // Apply a filter, reconciling by the item-set signature (its sorted valueKeys)
  // so re-applying the same cells doesn't stack a contradiction:
  //   same operator again → toggle off
  //   opposite operator   → flip the existing filter in place (include↔exclude)
  // A value therefore has exactly one state. Shared by hover +/- and bulk apply.
  function applyOrToggle(items, operator) {
    if (!items.length) return
    const sig = items.map(i => i.valueKey).sort().join('|')
    const existing = appliedFilters.value.find(
      f => f.items.map(i => i.valueKey).sort().join('|') === sig,
    )
    if (existing) {
      if (existing.operator === operator) {
        removeApplied(existing.id)
      } else {
        appliedFilters.value = appliedFilters.value.map(
          f => (f.id === existing.id ? { ...f, operator } : f),
        )
      }
      return
    }
    pushApplied(items, operator)
  }

  // Toggle one CONTAINS token into the shared OR/NOT group for its column. All
  // "+" tokens for a field collapse into a single ANYOF filter (rows containing
  // ANY of them); all "-" tokens into a single NOT filter (rows containing none
  // of them). This is what makes picking several anon providers read as an OR.
  //
  // A value is kept in exactly one state (include XOR exclude): re-toggling under
  // the same operator removes it; toggling under the opposite operator flips it
  // in place (moves it between the ANYOF and NOT group) rather than leaving a
  // contradictory `contains(x) AND NOT contains(x)`. Emptying a group drops it.
  function toggleContains(field, value, negate) {
    const targetOp = negate ? FILTER_OPERATORS.NOT : FILTER_OPERATORS.ANYOF
    const item = makeSelectionItem(field, value, String(value), QUEUE_MODES.CONTAINS, false)
    const vk = item.valueKey

    // A CONTAINS group for this field under a given operator.
    const groupFor = op => f =>
      f.operator === op &&
      f.items.length > 0 &&
      f.items.every(i => i.mode === QUEUE_MODES.CONTAINS && i.field === field)

    // Already in the target-operator group → toggle off.
    const target = appliedFilters.value.find(groupFor(targetOp))
    if (target && target.items.some(i => i.valueKey === vk)) {
      dropValueKey(vk)
      return
    }

    // Clear any prior occurrence (e.g. the opposite operator's group) so the
    // value ends up in exactly one state, then add it to the target group —
    // re-finding after the drop, which may have emptied it.
    dropValueKey(vk)
    const group = appliedFilters.value.find(groupFor(targetOp))
    if (group) {
      appliedFilters.value = appliedFilters.value.map(
        f => (f.id === group.id ? { ...f, items: [...f.items, item] } : f),
      )
    } else {
      pushApplied([item], targetOp)
    }
  }

  // Reduce rows through every applied filter. The host supplies `getValue(row,
  // field)` (row shapes differ) and an optional `containsValue(row, target)`
  // for value-only items.
  function filterRows(rows, getValue, containsValue) {
    return appliedFilters.value.reduce(
      (acc, f) =>
        acc.filter(row =>
          matchFilter(
            f,
            field => getValue(row, field),
            containsValue ? target => containsValue(row, target) : undefined,
          ),
        ),
      rows,
    )
  }

  // --- cross-navigation / clipboard (operate on an items[] arg so hover +/-
  // and the context menu share them) ---------------------------------------

  function openInEventSearch(items, operator = FILTER_OPERATORS.ANYOF) {
    if (!items.length) return
    const mappings = dataSourcesStore.fieldMappings
    const parts = items.map(item => buildQueryFragment(item, mappings))
    let query
    if (operator === FILTER_OPERATORS.NOT) {
      query = `NOT (${parts.join(' OR ')})`
    } else {
      const joiner = operator === FILTER_OPERATORS.ALL ? ' AND ' : ' OR '
      query = parts.join(joiner)
    }
    const routeQuery = { query }
    const sources = dataSourcesStore.serializedSources
    if (sources) routeQuery.sources = sources
    const resolved = router.resolve({ name: 'EventSearch', query: routeQuery })
    window.open(resolved.href, '_blank')
  }

  function openInSelectorSearch(items) {
    if (!items.length) return
    const values = [...new Set(items.map(item => String(item.rawValue)))]
    const resolved = router.resolve({ name: 'SelectorResults', query: { input: values.join(',') } })
    window.open(resolved.href, '_blank')
  }

  async function writeClipboard(text) {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
  }

  async function copyItems(items) {
    if (!items.length) return
    await writeClipboard(items.map(item => String(item.rawValue)).join(', '))
  }

  // Ctrl/Cmd+C: copy the selected cells as a spreadsheet-style TSV grid. The
  // stored cells carry rowIndex/colIndex, so we rebuild the dense rectangle they
  // span (tab between columns, newline between rows, blank for gaps) — this
  // pastes cleanly into Excel/Sheets. Hosts whose cells lack numeric indices
  // (synthetic keys) fall back to a newline-joined value list.
  async function copySelectionGrid() {
    const cells = [...selection.value.values()]
    if (!cells.length) return
    const cellText = c => String(c.formattedValue ?? c.rawValue ?? '')

    const indexed = cells.every(
      c => Number.isInteger(c.rowIndex) && Number.isInteger(c.colIndex),
    )
    if (!indexed) {
      await writeClipboard(cells.map(cellText).join('\n'))
      return
    }

    const rows = cells.map(c => c.rowIndex)
    const cols = cells.map(c => c.colIndex)
    const r0 = Math.min(...rows)
    const r1 = Math.max(...rows)
    const c0 = Math.min(...cols)
    const c1 = Math.max(...cols)

    const grid = new Map() // `${r}::${c}` -> text
    for (const c of cells) grid.set(`${c.rowIndex}::${c.colIndex}`, cellText(c))

    const lines = []
    for (let r = r0; r <= r1; r++) {
      const line = []
      for (let c = c0; c <= c1; c++) line.push(grid.get(`${r}::${c}`) ?? '')
      lines.push(line.join('\t'))
    }
    await writeClipboard(lines.join('\n'))
  }

  return {
    selection,
    anchor,
    isSelected,
    hasSelection,
    count,
    selectedItems,
    cellKey,
    setSingle,
    setSingleOrDeselect,
    toggle,
    setRange,
    clear,
    appliedFilters,
    removeApplied,
    clearApplied,
    applyOrToggle,
    toggleContains,
    filterRows,
    openInEventSearch,
    openInSelectorSearch,
    copyItems,
    copySelectionGrid,
  }
}
